/**
 * La barra de «varios a la vez», encima de la lista de alumnos · 03-11-2026
 *
 * Sale en cuanto hay alguien marcado. Cuatro cosas, las que pidió el primer club:
 * **meter en un grupo, poner una tarifa, cambiar el estado y poner la fecha de
 * alta**. Cada una abre su formulario corto, con un único botón que dice a cuántos
 * se aplica.
 *
 * Al terminar enseña cuántos se hicieron y **quiénes se saltaron y por qué** —«Lucía
 * Pérez: no tiene pagador»—, y deja marcados solo a esos, para arreglarlos y volver a
 * intentarlo sin buscarlos (`soloLosSaltados`).
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Error as PanelError } from '@/shared/ui/Estados';
import { euros } from '@/shared/ui/formato';
import { FaltaAntes } from '@/shared/ui/FaltaAntes';
import type { Grupo } from '@/club/alumnos/tipos';
import { etiquetaDePeriodo, type Tarifa } from '@/club/cuotas/tipos';
import {
  alumnosEnTexto,
  cambiosDeEstado,
  hoyISO,
  type EstadoEnBloque,
  type Seleccion,
} from '@/club/alumnos/varios/seleccion';

type Accion = 'grupo' | 'tarifa' | 'estado' | 'alta';

interface Resultado {
  readonly done: number;
  readonly skipped: readonly {
    readonly student_id: string;
    readonly name: string;
    readonly code: string;
    readonly message: string;
  }[];
}

const ACCIONES: readonly { id: Accion; texto: string }[] = [
  { id: 'grupo', texto: 'Meter en un grupo' },
  { id: 'tarifa', texto: 'Poner una tarifa' },
  { id: 'estado', texto: 'Cambiar el estado' },
  { id: 'alta', texto: 'Fecha de alta' },
];

const ESTADOS: readonly { id: EstadoEnBloque; texto: string }[] = [
  { id: 'active', texto: 'Activo' },
  { id: 'trial', texto: 'En prueba' },
  { id: 'paused', texto: 'En pausa' },
  { id: 'left', texto: 'De baja' },
];

export function AccionesEnBloque({
  club,
  seleccion,
  grupos,
  hayMasPaginas,
  eligiendoTodos,
  alElegirTodos,
  alQuitar,
  alTerminar,
}: {
  club: string;
  seleccion: Seleccion;
  grupos: readonly Grupo[];
  /** Hay más alumnos con este filtro que los de la página. */
  hayMasPaginas: boolean;
  eligiendoTodos: boolean;
  alElegirTodos: () => void;
  alQuitar: () => void;
  /** Con lo que respondió el servidor: la lista decide qué queda marcado. */
  alTerminar: (r: Resultado) => void;
}) {
  const cache = useQueryClient();
  const [accion, setAccion] = useState<Accion | null>(null);
  const [resultado, setResultado] = useState<Resultado | null>(null);
  const [problema, setProblema] = useState<Problema | null>(null);

  // Los campos de los cuatro formularios. Pocos y cortos: no merece un reductor.
  const [grupoId, setGrupoId] = useState('');
  const [tarifaId, setTarifaId] = useState('');
  const [cambiarLasQueHay, setCambiarLasQueHay] = useState(false);
  const [estado, setEstado] = useState<EstadoEnBloque>('active');
  const [motivo, setMotivo] = useState('');
  const [fecha, setFecha] = useState(hoyISO);

  const ids = [...seleccion.keys()];
  const cuantos = alumnosEnTexto(ids.length);

  const tarifas = useQuery({
    queryKey: ['tarifas', club, false],
    enabled: accion === 'tarifa',
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club })['fee-plans'].get({
        query: {},
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Tarifa[];
    },
  });

  const aplicar = useMutation({
    mutationFn: async (): Promise<Resultado> => {
      const clubApi = api.v1.clubs({ club });
      const r =
        accion === 'grupo'
          ? await clubApi.groups({ groupId: grupoId }).enrollments.bulk.post({
              student_ids: ids,
              joined_on: fecha,
            })
          : accion === 'tarifa'
            ? await clubApi.subscriptions.bulk.post({
                student_ids: ids,
                fee_plan_id: tarifaId,
                starts_on: fecha,
                replace_existing: cambiarLasQueHay,
              })
            : await clubApi.students['bulk-update'].post({
                student_ids: ids,
                changes:
                  accion === 'estado'
                    ? cambiosDeEstado(estado, fecha, motivo)
                    : { joined_on: fecha },
              });
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as Resultado;
    },
    onSuccess: (r) => {
      setResultado(r);
      setAccion(null);
      // Lo que cambia con esto: la lista, la ocupación de los grupos, las cuotas y
      // las cuentas de Inicio.
      for (const clave of [
        'alumnos',
        'alumno',
        'grupos',
        'grupo',
        'matriculas',
        'cuotas',
        'cuota',
        'desglose',
        'hoy',
      ]) {
        void cache.invalidateQueries({ queryKey: [clave] });
      }
      alTerminar(r);
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const abrir = (a: Accion) => {
    setProblema(null);
    setResultado(null);
    setFecha(hoyISO());
    setAccion(accion === a ? null : a);
  };

  const listo =
    ids.length > 0 &&
    fecha !== '' &&
    (accion === 'grupo'
      ? grupoId !== ''
      : accion === 'tarifa'
        ? tarifaId !== ''
        : true);

  const verbo: Record<Accion, string> = {
    grupo: `Meter a ${cuantos} en el grupo`,
    tarifa: `Poner la tarifa a ${cuantos}`,
    estado: `Cambiar el estado de ${cuantos}`,
    alta: `Poner la fecha de alta a ${cuantos}`,
  };

  return (
    <div className="en-bloque">
      {resultado && (
        <div
          className={resultado.skipped.length > 0 ? 'aviso' : 'nota nota-bien'}
          role="status"
        >
          <p>
            <strong>
              {resultado.done === 0
                ? 'No se ha cambiado ninguno.'
                : `Hecho con ${alumnosEnTexto(resultado.done)}.`}
            </strong>
            {resultado.skipped.length > 0 &&
              ` ${resultado.skipped.length === 1 ? 'Uno se ha saltado' : `${resultado.skipped.length} se han saltado`}, y ${resultado.skipped.length === 1 ? 'se queda marcado' : 'se quedan marcados'} para que puedas arreglarlo:`}
          </p>
          {resultado.skipped.length > 0 && (
            <ul className="saltados-en-bloque">
              {resultado.skipped.map((s) => (
                <li key={s.student_id}>
                  <strong>{s.name || 'Alumno que no es del club'}</strong>:{' '}
                  {s.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {ids.length > 0 && (
        <div
          className="aviso barra-en-bloque"
          role="region"
          aria-label="Varios a la vez"
        >
          <p>
            <strong>
              {ids.length === 1
                ? '1 alumno elegido'
                : `${ids.length} alumnos elegidos`}
            </strong>
            {hayMasPaginas && (
              <>
                {' · '}
                <button
                  type="button"
                  className="enlace-fila"
                  disabled={eligiendoTodos}
                  onClick={alElegirTodos}
                >
                  {eligiendoTodos
                    ? 'Eligiendo…'
                    : 'Elegir también los de las demás páginas'}
                </button>
              </>
            )}
          </p>
          <div className="hueco-acciones">
            {ACCIONES.map((a) => (
              <button
                key={a.id}
                type="button"
                className={accion === a.id ? undefined : 'btn-plano'}
                aria-pressed={accion === a.id}
                onClick={() => abrir(a.id)}
              >
                {a.texto}
              </button>
            ))}
            <button type="button" className="btn-plano" onClick={alQuitar}>
              Quitar la selección
            </button>
          </div>
        </div>
      )}

      {accion && ids.length > 0 && (
        <form
          className="formulario"
          onSubmit={(e) => {
            e.preventDefault();
            setProblema(null);
            aplicar.mutate();
          }}
        >
          <div className="rejilla">
            {accion === 'grupo' && (
              <label>
                Grupo
                <select
                  required
                  value={grupoId}
                  onChange={(e) => setGrupoId(e.target.value)}
                >
                  <option value="">Elige un grupo</option>
                  {grupos
                    .filter((g) => g.active)
                    .map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                        {g.venue_name ? ` · ${g.venue_name}` : ''} (
                        {g.enrolled_count} dentro)
                      </option>
                    ))}
                </select>
              </label>
            )}

            {accion === 'tarifa' && (
              <label>
                Tarifa
                <select
                  required
                  value={tarifaId}
                  onChange={(e) => setTarifaId(e.target.value)}
                  disabled={!tarifas.data}
                >
                  <option value="">Elige una tarifa</option>
                  {(tarifas.data ?? [])
                    .filter((t) => t.active)
                    .map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name} · {euros(t.amount_cents)} ·{' '}
                        {etiquetaDePeriodo(t.period).toLowerCase()}
                      </option>
                    ))}
                </select>
              </label>
            )}

            {accion === 'estado' && (
              <label>
                Estado
                <select
                  value={estado}
                  onChange={(e) => setEstado(e.target.value as EstadoEnBloque)}
                >
                  {ESTADOS.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.texto}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {(accion !== 'estado' || estado === 'left') && (
              <label>
                {accion === 'grupo'
                  ? 'Desde'
                  : accion === 'tarifa'
                    ? 'Empieza'
                    : accion === 'estado'
                      ? 'Fecha de la baja'
                      : 'Fecha de alta'}
                <input
                  type="date"
                  required
                  value={fecha}
                  onChange={(e) => setFecha(e.target.value)}
                />
              </label>
            )}

            {accion === 'estado' && estado === 'left' && (
              <label>
                Motivo
                <input
                  maxLength={500}
                  placeholder="Fin de temporada"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
              </label>
            )}
          </div>

          {accion === 'grupo' &&
            grupos.filter((g) => g.active).length === 0 && (
              <FaltaAntes
                accion="Crear el primer grupo"
                destino="/grupos"
                nuevo
              >
                Tu club todavía no tiene grupos.
              </FaltaAntes>
            )}

          {accion === 'tarifa' &&
            tarifas.data?.filter((t) => t.active).length === 0 && (
              <FaltaAntes
                accion="Crear la primera tarifa"
                destino="/cobros/tarifas"
                nuevo
                volver="/alumnos"
              >
                Tu club todavía no tiene tarifas.
              </FaltaAntes>
            )}

          {accion === 'tarifa' && (
            <>
              <label className="opcion">
                <input
                  type="checkbox"
                  checked={cambiarLasQueHay}
                  onChange={(e) => setCambiarLasQueHay(e.target.checked)}
                />
                <span>
                  <strong>Cambiar también la de los que ya tienen cuota</strong>
                  <em>
                    Sin marcarla, a quien ya tiene cuota se le deja como está.
                    Marcada, se le cambia la tarifa y conserva su pagador y su
                    precio pactado.
                  </em>
                </span>
              </label>
              <p className="tenue">
                Cada cuota la paga <strong>el pagador que ya tiene</strong> ese
                alumno, el de su tutor. A quien no tenga pagador, o tenga
                varios, se le salta y se dice.
              </p>
            </>
          )}

          {accion === 'grupo' && (
            <p className="tenue">
              Los que ya están en el grupo se saltan. Si no caben todos, no
              entra ninguno y se dice cuántas plazas quedan.
            </p>
          )}

          {problema && <PanelError problema={problema} />}

          <div className="acciones">
            <button type="submit" disabled={!listo || aplicar.isPending}>
              {aplicar.isPending ? 'Aplicando…' : verbo[accion]}
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setAccion(null)}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
